import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormControlLabel, Switch, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import moment from 'moment';
import EXTERNAL_URLS from 'constants/externalUrls';
import InfoWithTooltip from 'components/InfoWithTooltip/InfoWithTooltip';
import Tabs from 'components/Tabs/Tabs';
import { get } from 'helpers/apiHelpers';
import { isEmpty } from 'lodash';

import { RangeTabsContext } from './contexts/RangeTabsContext';
import Charts from './Charts/Charts';
import { MODE, PERIOD_CONST, RANGE_MODE } from './consts';

const useStyles = makeStyles({
  tooltip: { cursor: 'pointer' },
  tabsSectionLabelSwitch: {
    margin: '0',
  },
  contentAfterTabButtons: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexGrow: '1',
    gap: '10px',
  },
  comparisonWrapper: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '4px',
  },
});

const dateFormat = 'DD-MM-YYYY';

const calculationDate = mode => {
  const today = moment(new Date());

  switch (mode) {
    case MODE.today:
      return {
        dateFrom: today.format(dateFormat),
        dateTo: today.format(dateFormat),
      };

    case MODE.sevenDays:
      return {
        dateFrom: today.clone().subtract(6, 'days').format(dateFormat),
        dateTo: today.format(dateFormat),
      };

    case MODE.thirtyDays:
      return {
        dateFrom: today.clone().subtract(29, 'days').format(dateFormat),
        dateTo: today.format(dateFormat),
      };

    default:
      return {
        dateFrom: today.clone().startOf(mode).format(dateFormat),
        dateTo: today.clone().endOf(mode).format(dateFormat),
      };
  }
};

const filterObject = (obj, allowedKeys) =>
  Object.fromEntries(
    Object.entries(obj).filter(([key]) => allowedKeys.includes(key))
  );

const metricsStatisticsFields = [
  'deliveryBags',
  'orderBags',
  'ordersCount',
  'ordersIncome',
];
const usersStatisticsFields = ['newClients', 'returningClients'];
const knowAboutUsStatisticsFields = ['knowAboutUs'];

const GET_STATISTICS_URL = '/v2/statistics';

const Dashboard = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { rangeActiveIndex, setRangeActiveIndex } =
    useContext(RangeTabsContext);
  const [showComparisons, setShowComparisons] = useState(false);
  const [statistics, setStatistics] = useState({});
  const [comparisonsStatisticsLastYear, setComparisonsStatisticsLastYear] =
    useState({});
  const [
    comparisonsStatisticsLastFiveYears,
    setComparisonsStatisticsLastFiveYears,
  ] = useState([]);
  const [isLoadingStatistics, setIsLoadingStatistics] = useState(false);
  const [isLoadingComparisonData, setIsLoadingComparisonData] = useState(false);
  const toggleShowComparisons = () => setShowComparisons(prev => !prev);

  const fetchStatisticsData = async activeIndex => {
    const period =
      RANGE_MODE[activeIndex] === MODE.year
        ? PERIOD_CONST.monthly
        : PERIOD_CONST.daily;
    const { dateFrom, dateTo } = calculationDate(RANGE_MODE[activeIndex]);

    try {
      setIsLoadingStatistics(true);

      const { statistics: currentStatistics } = await get(GET_STATISTICS_URL, {
        dateFrom,
        dateTo,
        fields: [
          'returningClients',
          'newClients',
          'knowAboutUs',
          'ordersIncome',
          'ordersCount',
          'orderBags',
          'deliveryBags',
        ],
        period,
      });

      setStatistics(currentStatistics);
    } catch (error) {
      // eslint-disable-next-line no-console -- FIXME
      console.error('Error fetching statistics:', error);
    } finally {
      setIsLoadingStatistics(false);
    }
  };

  const fetchComparisonsData = async activeIndex => {
    const { dateFrom, dateTo } = calculationDate(RANGE_MODE[activeIndex]);

    const isYearMode = RANGE_MODE[activeIndex] === MODE.year;
    const isWeekMode = RANGE_MODE[activeIndex] === MODE.isoWeek;

    const period = isYearMode ? PERIOD_CONST.monthly : PERIOD_CONST.daily;
    const daysToSubtract = 365;

    const getDate = (yearMultiplier = 0) => {
      if (yearMultiplier === 0) {
        const lastYearDateFrom = moment(dateFrom, dateFormat).subtract(
          daysToSubtract,
          'days'
        );
        const lastYearDateTo = moment(dateTo, dateFormat).subtract(
          daysToSubtract,
          'days'
        );

        if (isWeekMode) {
          let monday = lastYearDateFrom.clone();
          const dayOfWeek = monday.isoWeekday();

          if (dayOfWeek !== 1) {
            const daysToPrevMonday = dayOfWeek - 1;
            const daysToNextMonday = 8 - dayOfWeek;

            if (daysToPrevMonday <= daysToNextMonday) {
              monday = monday.subtract(daysToPrevMonday, 'days');
            } else {
              monday = monday.add(daysToNextMonday, 'days');
            }
          }
          const sunday = monday.clone().add(6, 'days');

          return {
            prevYearDateFrom: monday.format(dateFormat),
            prevYearDateTo: sunday.format(dateFormat),
          };
        }

        return {
          prevYearDateFrom: lastYearDateFrom.format(dateFormat),
          prevYearDateTo: lastYearDateTo.format(dateFormat),
        };
      }

      const targetYear = moment().year() - yearMultiplier;

      return {
        prevYearDateFrom: moment()
          .year(targetYear)
          .startOf('year')
          .format(dateFormat),

        prevYearDateTo: moment()
          .year(targetYear)
          .endOf('year')
          .format(dateFormat),
      };
    };

    const createParams = (previousYear = 0) => ({
      dateFrom: getDate(previousYear).prevYearDateFrom,
      dateTo: getDate(previousYear).prevYearDateTo,
      fields: [
        'returningClients',
        'newClients',
        'ordersIncome',
        'ordersCount',
        'orderBags',
        'deliveryBags',
      ],
      period,
    });

    try {
      setIsLoadingComparisonData(true);

      if (isYearMode) {
        const responseLastFiveYears = await Promise.all(
          [...Array(4).keys()].map(i =>
            get(GET_STATISTICS_URL, createParams(i + 1))
          )
        );

        setComparisonsStatisticsLastFiveYears(
          responseLastFiveYears.map(response => response.statistics)
        );
      } else {
        const { statistics: currentStatistics } = await get(
          GET_STATISTICS_URL,
          createParams()
        );
        setComparisonsStatisticsLastYear(currentStatistics);
      }
    } catch (error) {
      // eslint-disable-next-line no-console -- FIXME
      console.error('Error fetching comparisons statistics:', error);
    } finally {
      setIsLoadingComparisonData(false);
    }
  };

  useEffect(() => {
    fetchStatisticsData(rangeActiveIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
  }, []);

  const renderDateWithWeekDay = ({ dateFrom }) =>
    moment(dateFrom).format('ddd. DD.MM').toLowerCase();
  const renderShortDate = ({ dateFrom }) => moment(dateFrom).format('DD.MM');
  const renderMonths = ({ dateFrom }) => {
    const month = moment(dateFrom).format('MMM');

    return month.charAt(0).toUpperCase() + month.slice(1);
  };
  const renderDatesComparison = ({ firstDate, secondDate }) => {
    if (
      moment(firstDate).format('DD.MM') === moment(secondDate).format('DD.MM')
    ) {
      return renderShortDate({ dateFrom: firstDate });
    }

    const twoDates = [
      moment(firstDate).format('DD.MM.YY'),
      moment(secondDate).format('DD.MM.YY'),
    ];

    if (moment(firstDate).day() === moment(secondDate).day()) {
      twoDates.unshift(moment(firstDate).format('ddd.'));
    }

    return twoDates;
  };

  const generateDate = mode => {
    if (isEmpty(statistics)) {
      return null;
    }

    const earliestGeneratedData = Object.values(statistics)
      .map(el => new Date(el[mode]))
      .reduce((earliest, current) => (current < earliest ? current : earliest));

    return moment(earliestGeneratedData).format('HH:mm');
  };

  const invalidateAtTime = generateDate('invalidateAt');
  const generatedAtTime = generateDate('generatedAt');

  const metricStatistics = filterObject(statistics, metricsStatisticsFields);
  const usersStatistics = filterObject(statistics, usersStatisticsFields);
  const knowAboutUsStatistics = filterObject(
    statistics,
    knowAboutUsStatisticsFields
  );
  const metricsComparisonsStatisticsLastYear = filterObject(
    comparisonsStatisticsLastYear,
    metricsStatisticsFields
  );
  const usersComparisonsStatisticsLastYear = filterObject(
    comparisonsStatisticsLastYear,
    usersStatisticsFields
  );

  const metricsComparisonsStatisticsLastFiveYears =
    comparisonsStatisticsLastFiveYears.map(statistic =>
      filterObject(statistic, metricsStatisticsFields)
    );
  const usersComparisonsStatisticsLastFiveYears =
    comparisonsStatisticsLastFiveYears.map(statistic =>
      filterObject(statistic, usersStatisticsFields)
    );

  const tabsConfig = [
    {
      tooltip: '',
      label: t('dashboard.tabs.label.today'),
      metricsStatistics: [
        metricStatistics,
        showComparisons && metricsComparisonsStatisticsLastYear,
      ],
      renderMetricsLabels: renderDateWithWeekDay,
      usersStatistics: [
        usersStatistics,
        showComparisons && usersComparisonsStatisticsLastYear,
      ],
      renderUsersLabels: renderDateWithWeekDay,
      knowAboutUsStatistics,
    },
    {
      tooltip: '',
      label: t('dashboard.tabs.label.last7days'),
      metricsStatistics: [
        metricStatistics,
        showComparisons && metricsComparisonsStatisticsLastYear,
      ],
      renderMetricsLabels: renderDateWithWeekDay,
      usersStatistics: [
        usersStatistics,
        showComparisons && usersComparisonsStatisticsLastYear,
      ],
      renderUsersLabels: renderShortDate,
      knowAboutUsStatistics,
    },
    {
      tooltip: '',
      label: t('dashboard.tabs.label.last30days'),
      metricsStatistics: [
        metricStatistics,
        showComparisons && metricsComparisonsStatisticsLastYear,
      ],
      renderMetricsLabels: renderShortDate,
      renderMetricsComparisonsLabels: renderDatesComparison,
      usersStatistics: [
        usersStatistics,
        showComparisons && usersComparisonsStatisticsLastYear,
      ],
      renderUsersLabels: renderShortDate,
      knowAboutUsStatistics,
    },
    {
      tooltip: '',
      label: t('dashboard.tabs.label.week'),
      metricsStatistics: [
        metricStatistics,
        showComparisons && metricsComparisonsStatisticsLastYear,
      ],
      renderMetricsLabels: renderDateWithWeekDay,
      renderMetricsComparisonsLabels: renderDatesComparison,
      usersStatistics: [
        usersStatistics,
        showComparisons && usersComparisonsStatisticsLastYear,
      ],
      renderUsersLabels: renderShortDate,
      knowAboutUsStatistics,
    },
    {
      tooltip: '',
      label: t('dashboard.tabs.label.month'),
      metricsStatistics: [
        metricStatistics,
        showComparisons && metricsComparisonsStatisticsLastYear,
      ],
      renderMetricsLabels: renderShortDate,
      usersStatistics: [
        usersStatistics,
        showComparisons && usersComparisonsStatisticsLastYear,
      ],
      renderUsersLabels: renderShortDate,
      knowAboutUsStatistics,
    },
    {
      tooltip: '',
      label: t('dashboard.tabs.label.year'),
      metricsStatistics: [
        metricStatistics,
        ...(showComparisons ? metricsComparisonsStatisticsLastFiveYears : []),
      ],
      renderMetricsLabels: renderMonths,
      usersStatistics: [
        usersStatistics,
        ...(showComparisons ? usersComparisonsStatisticsLastFiveYears : []),
      ],
      renderUsersLabels: renderMonths,
      knowAboutUsStatistics,
    },
  ];

  const isLoading = isLoadingStatistics || isLoadingComparisonData;

  return (
    <>
      <Tabs
        tabs={tabsConfig.map(
          ({
            tooltip,
            label,
            metricsStatistics,
            renderMetricsLabels,
            renderMetricsComparisonsLabels,
            // eslint-disable-next-line no-shadow -- FIXME
            usersStatistics,
            renderUsersLabels,
            // eslint-disable-next-line no-shadow -- FIXME
            knowAboutUsStatistics,
          }) => ({
            tooltip,
            label,
            isDisabled: isLoading,
            content: (
              <Charts
                isLoading={isLoading}
                metricsStatistics={metricsStatistics.filter(Boolean)}
                renderMetricsLabels={renderMetricsLabels}
                renderMetricsComparisonsLabels={
                  showComparisons ? renderMetricsComparisonsLabels : undefined
                }
                renderUsersLabels={renderUsersLabels}
                usersStatistics={usersStatistics.filter(Boolean)}
                knowAboutUsStatistics={knowAboutUsStatistics}
              />
            ),
          })
        )}
        contentAfterTabButtons={
          <div className={classes.contentAfterTabButtons}>
            <div className={classes.comparisonWrapper}>
              <FormControlLabel
                className={classes.tabsSectionLabelSwitch}
                label={t('dashboard.toggle.label')}
                control={
                  <Switch
                    checked={showComparisons}
                    onChange={() => {
                      toggleShowComparisons();
                      // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- FIXME
                      !showComparisons &&
                        fetchComparisonsData(rangeActiveIndex);
                    }}
                  />
                }
              />
              <InfoWithTooltip
                title={t('dashboard.statisticsPanelComparisonInfo')}
                link={EXTERNAL_URLS.statisticsPanelComparisons}
              />
            </div>
            <Tooltip
              className={classes.tooltip}
              title={
                invalidateAtTime
                  ? t('dashboard.nextUpdate', {
                      time: invalidateAtTime,
                    })
                  : ''
              }
            >
              <span>
                {generatedAtTime
                  ? t('dashboard.lastUpdate', {
                      time: generatedAtTime,
                    })
                  : ''}
              </span>
            </Tooltip>
          </div>
        }
        activeIndex={rangeActiveIndex}
        setActiveIndex={activeIndex => {
          fetchStatisticsData(activeIndex);
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- FIXME
          showComparisons && fetchComparisonsData(activeIndex);
          setRangeActiveIndex(activeIndex);
        }}
      />
    </>
  );
};

export default Dashboard;
